import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	Calendar as CalendarIcon,
	CalendarDays,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";
import { visitMatchesAcademicYear } from "../../utils/academicYear";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EMPLOYEE_COLORS = [
	"#6366f1",
	"#0891b2",
	"#059669",
	"#d97706",
	"#db2777",
	"#7c3aed",
	"#2563eb",
	"#dc2626",
];

const getVisitEmployee = (visit) =>
	visit.visited_by_profile?.full_name ||
	visit.planned_by_profile?.full_name ||
	"Unassigned";

const getVisitEmployeeId = (visit) =>
	visit.visited_by || visit.planned_by || "unassigned";

const formatVisitDateTime = (date) =>
	date ? dayjs(date).format("DD/MM/YYYY HH:mm") : null;

const getVisitStatusColor = (visit) => {
	if (!visit.planned_date) return { bg: "#f3f4f6", border: "#d1d5db" };

	const today = dayjs().startOf("day");
	const plannedDate = dayjs(visit.planned_date).startOf("day");
	const visitedDate = visit.visited_date
		? dayjs(visit.visited_date).startOf("day")
		: null;
	const isDone = visit.visit_status?.name === "Done" || !!visitedDate;

	if (isDone) {
		if (visitedDate && visitedDate.isAfter(plannedDate)) {
			return { bg: "#fff3e0", border: "#fb8c00" };
		}
		return { bg: "#e8f5e9", border: "#43a047" };
	}

	if (today.isAfter(plannedDate)) {
		return { bg: "#ffebee", border: "#e53935" };
	}

	return { bg: "#e3f2fd", border: "#1e88e5" };
};

const buildCalendarDays = (month) => {
	const startOfMonth = month.startOf("month");
	const endOfMonth = month.endOf("month");
	// Align to Sun–Sat header (Sunday = 0)
	const gridStart = startOfMonth.subtract(startOfMonth.day(), "day");
	const gridEnd = endOfMonth.add(6 - endOfMonth.day(), "day");

	const days = [];
	let current = gridStart;

	while (current.isBefore(gridEnd) || current.isSame(gridEnd, "day")) {
		days.push(current);
		current = current.add(1, "day");
	}

	return days;
};

const VISITS_COLLAPSED_LIMIT = 3;
const DAY_VIEW_START_HOUR = 6;
const DAY_VIEW_END_HOUR = 20;

const isUntimedVisit = (visit) => {
	if (!visit.planned_date) return true;
	const planned = dayjs(visit.planned_date);
	return planned.hour() === 0 && planned.minute() === 0;
};

const groupDayVisitsByHour = (dayVisits) => {
	const untimed = [];
	const byHour = {};

	dayVisits.forEach((visit) => {
		if (!visit.planned_date) {
			untimed.push(visit);
			return;
		}

		if (isUntimedVisit(visit)) {
			untimed.push(visit);
			return;
		}

		const hour = dayjs(visit.planned_date).hour();
		if (!byHour[hour]) byHour[hour] = [];
		byHour[hour].push(visit);
	});

	Object.values(byHour).forEach((hourVisits) => {
		hourVisits.sort(
			(a, b) => dayjs(a.planned_date).valueOf() - dayjs(b.planned_date).valueOf()
		);
	});

	const visitHours = Object.keys(byHour).map(Number);
	const minHour = visitHours.length
		? Math.min(DAY_VIEW_START_HOUR, ...visitHours)
		: DAY_VIEW_START_HOUR;
	const maxHour = visitHours.length
		? Math.max(DAY_VIEW_END_HOUR, ...visitHours)
		: DAY_VIEW_END_HOUR;

	const hours = [];
	for (let hour = minHour; hour <= maxHour; hour += 1) {
		hours.push(hour);
	}

	return { hours, byHour, untimed };
};

const DayHourView = ({ dayVisits, employeeColorMap }) => {
	const { hours, byHour, untimed } = useMemo(
		() => groupDayVisitsByHour(dayVisits),
		[dayVisits]
	);

	if (dayVisits.length === 0) {
		return (
			<Box sx={{ py: 8, textAlign: "center" }}>
				<Typography variant="body2" color="text.secondary">
					No visits scheduled for this day.
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			{untimed.length > 0 && (
				<Box
					sx={{
						display: "flex",
						borderBottom: "1px solid #e5e7eb",
						bgcolor: "#fafafa",
					}}>
					<Box
						sx={{
							width: 88,
							flexShrink: 0,
							p: 1.5,
							borderRight: "1px solid #e5e7eb",
							bgcolor: "#f9fafb",
						}}>
						<Typography variant="caption" fontWeight={600} color="text.secondary">
							No time
						</Typography>
					</Box>
					<Box sx={{ flex: 1, p: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
						{untimed.map((visit) => (
							<VisitCard
								key={visit.id}
								visit={visit}
								employeeColorMap={employeeColorMap}
							/>
						))}
					</Box>
				</Box>
			)}

			{hours.map((hour) => {
				const hourVisits = byHour[hour] || [];

				return (
					<Box
						key={hour}
						sx={{
							display: "flex",
							borderBottom: "1px solid #e5e7eb",
							minHeight: 64,
							bgcolor: hourVisits.length > 0 ? "#fff" : "#fcfcfc",
						}}>
						<Box
							sx={{
								width: 88,
								flexShrink: 0,
								p: 1.5,
								borderRight: "1px solid #e5e7eb",
								bgcolor: "#f9fafb",
							}}>
							<Typography variant="caption" fontWeight={600}>
								{String(hour).padStart(2, "0")}:00
							</Typography>
						</Box>
						<Box
							sx={{
								flex: 1,
								p: 1.5,
								display: "flex",
								flexDirection: "column",
								gap: 0.75,
							}}>
							{hourVisits.length === 0 ? (
								<Typography variant="caption" color="text.disabled" sx={{ pt: 0.25 }}>
									—
								</Typography>
							) : (
								hourVisits.map((visit) => (
									<Box
										key={visit.id}
										sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ minWidth: 42, pt: 0.35, fontWeight: 600 }}>
											{dayjs(visit.planned_date).format("HH:mm")}
										</Typography>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<VisitCard
												visit={visit}
												employeeColorMap={employeeColorMap}
											/>
										</Box>
									</Box>
								))
							)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};

const VisitCard = ({ visit, employeeColorMap }) => {
	const statusColor = getVisitStatusColor(visit);
	const employeeId = getVisitEmployeeId(visit);
	const employeeColor = employeeColorMap[employeeId] || "#6b7280";

	return (
		<Tooltip
			title={
				<Box>
					<Typography variant="caption" display="block">
						{visit.schools?.name}
					</Typography>
					<Typography variant="caption" display="block">
						{getVisitEmployee(visit)}
					</Typography>
					<Typography variant="caption" display="block">
						{visit.visit_types?.name} · {visit.visit_status?.name || "Pending"}
					</Typography>
					{visit.planned_date && (
						<Typography variant="caption" display="block">
							Planned: {formatVisitDateTime(visit.planned_date)}
						</Typography>
					)}
					{visit.visited_date && (
						<Typography variant="caption" display="block">
							Visited: {formatVisitDateTime(visit.visited_date)}
						</Typography>
					)}
				</Box>
			}
			arrow>
			<Box
				sx={{
					px: 0.75,
					py: 0.25,
					borderRadius: 0.5,
					bgcolor: statusColor.bg,
					borderTop: `1px solid ${statusColor.border}`,
					borderRight: `1px solid ${statusColor.border}`,
					borderBottom: `1px solid ${statusColor.border}`,
					borderLeft: `3px solid ${employeeColor}`,
					overflow: "hidden",
				}}>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 600,
						lineHeight: 1.2,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}>
					{getVisitEmployee(visit)}
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{
						display: "block",
						lineHeight: 1.2,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						fontSize: "0.65rem",
					}}>
					{visit.schools?.name}
				</Typography>
			</Box>
		</Tooltip>
	);
};

const SchoolVisitTracker = () => {
	const { supabase } = useAuth();
	const { selectedAcademicYear, setSelectedAcademicYear } =
		useSelectedAcademicYear();

	const [currentMonth, setCurrentMonth] = useState(dayjs().startOf("month"));
	const [visits, setVisits] = useState([]);
	const [visitStatuses, setVisitStatuses] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [employeeFilter, setEmployeeFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [selectedDay, setSelectedDay] = useState(null);
	const [currentDay, setCurrentDay] = useState(dayjs().startOf("day"));
	const [viewMode, setViewMode] = useState("month");
	const [expandedRowIndex, setExpandedRowIndex] = useState(null);

	const fetchVisits = useCallback(async () => {
		try {
			setLoading(true);
			setError("");

			const { data, error: fetchError } = await supabase
				.from("school_visits")
				.select(
					`
					*,
					schools (id, name),
					planned_by_profile:profiles!planned_by (id, full_name),
					visited_by_profile:profiles!visited_by (id, full_name),
					visit_types (id, name, visit_type),
					visit_status (id, name)
				`
				)
				.order("planned_date", { ascending: true });

			if (fetchError) throw fetchError;
			setVisits(data || []);
		} catch (err) {
			console.error("Error fetching school visits:", err);
			setError("Failed to load school visits.");
		} finally {
			setLoading(false);
		}
	}, [supabase]);

	const fetchVisitStatuses = useCallback(async () => {
		try {
			const { data, error: fetchError } = await supabase
				.from("visit_status")
				.select("*")
				.order("name");

			if (fetchError) throw fetchError;
			setVisitStatuses(data || []);
		} catch (err) {
			console.error("Error fetching visit statuses:", err);
		}
	}, [supabase]);

	useEffect(() => {
		fetchVisits();
		fetchVisitStatuses();
	}, [fetchVisits, fetchVisitStatuses]);

	const filteredVisits = useMemo(() => {
		return visits.filter((visit) => {
			if (!visitMatchesAcademicYear(visit, selectedAcademicYear)) {
				return false;
			}
			if (employeeFilter && getVisitEmployeeId(visit) !== employeeFilter) {
				return false;
			}
			if (statusFilter && visit.visit_status_id !== statusFilter) {
				return false;
			}
			return true;
		});
	}, [visits, selectedAcademicYear, employeeFilter, statusFilter]);

	const visitsByDate = useMemo(() => {
		const map = {};
		filteredVisits.forEach((visit) => {
			if (!visit.planned_date) return;
			const key = dayjs(visit.planned_date).format("YYYY-MM-DD");
			if (!map[key]) map[key] = [];
			map[key].push(visit);
		});
		return map;
	}, [filteredVisits]);

	const employeeOptions = useMemo(() => {
		const employees = new Map();
		visits
			.filter((visit) => visitMatchesAcademicYear(visit, selectedAcademicYear))
			.forEach((visit) => {
				const id = getVisitEmployeeId(visit);
				const name = getVisitEmployee(visit);
				if (id !== "unassigned") {
					employees.set(id, name);
				}
			});
		return Array.from(employees.entries())
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [visits, selectedAcademicYear]);

	const employeeColorMap = useMemo(() => {
		const map = {};
		employeeOptions.forEach((employee, index) => {
			map[employee.id] = EMPLOYEE_COLORS[index % EMPLOYEE_COLORS.length];
		});
		return map;
	}, [employeeOptions]);

	const calendarDays = useMemo(
		() => buildCalendarDays(currentMonth),
		[currentMonth]
	);

	const monthVisitsCount = useMemo(() => {
		return filteredVisits.filter((visit) =>
			dayjs(visit.planned_date).isSame(currentMonth, "month")
		).length;
	}, [filteredVisits, currentMonth]);

	const selectedDayVisits = selectedDay
		? visitsByDate[selectedDay.format("YYYY-MM-DD")] || []
		: [];

	const dayViewVisits = useMemo(() => {
		const dateKey = currentDay.format("YYYY-MM-DD");
		return visitsByDate[dateKey] || [];
	}, [visitsByDate, currentDay]);

	const dayVisitsCount = dayViewVisits.length;

	const handlePrevMonth = () => {
		setCurrentMonth((prev) => prev.subtract(1, "month"));
		setSelectedDay(null);
		setExpandedRowIndex(null);
	};

	const handleNextMonth = () => {
		setCurrentMonth((prev) => prev.add(1, "month"));
		setSelectedDay(null);
		setExpandedRowIndex(null);
	};

	const handleToday = () => {
		const today = dayjs().startOf("day");
		setCurrentMonth(dayjs().startOf("month"));
		setCurrentDay(today);
		setSelectedDay(today);
	};

	const handlePrevDay = () => {
		setCurrentDay((prev) => prev.subtract(1, "day"));
		setSelectedDay((prev) => (prev ? prev.subtract(1, "day") : null));
	};

	const handleNextDay = () => {
		setCurrentDay((prev) => prev.add(1, "day"));
		setSelectedDay((prev) => (prev ? prev.add(1, "day") : null));
	};

	const handleViewModeChange = (_, nextMode) => {
		if (!nextMode) return;
		setViewMode(nextMode);
		if (nextMode === "day") {
			const day = selectedDay || dayjs().startOf("day");
			setCurrentDay(day);
			setSelectedDay(day);
		}
	};

	const handleDaySelect = (day) => {
		const normalizedDay = day.startOf("day");
		setSelectedDay(normalizedDay);
		setCurrentDay(normalizedDay);
	};

	const handleOpenDayView = () => {
		setCurrentDay(selectedDay || dayjs().startOf("day"));
		setViewMode("day");
	};

	const handleExpandRow = (event, weekIndex, day) => {
		event.stopPropagation();
		setExpandedRowIndex(weekIndex);
		handleDaySelect(day);
	};

	const handleCollapseRow = (event) => {
		event.stopPropagation();
		setExpandedRowIndex(null);
	};

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					mb: 3,
					flexWrap: "wrap",
					gap: 2,
				}}>
				<Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
						<CalendarIcon size={22} color="#6366f1" />
						<Typography variant="h6">School Visit Tracker</Typography>
					</Box>
					<Typography variant="body2" color="text.secondary">
						{viewMode === "month"
							? "Monthly calendar of planned school visits and assigned employees"
							: "Hourly schedule of school visits for the selected day"}
					</Typography>
				</Box>

				<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
					<AcademicYearSelector
						value={selectedAcademicYear}
						onChange={setSelectedAcademicYear}
					/>
					<FormControl size="small" sx={{ minWidth: 180 }}>
						<InputLabel>Employee</InputLabel>
						<Select
							value={employeeFilter}
							label="Employee"
							onChange={(e) => setEmployeeFilter(e.target.value)}>
							<MenuItem value="">All Employees</MenuItem>
							{employeeOptions.map((employee) => (
								<MenuItem key={employee.id} value={employee.id}>
									{employee.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Status</InputLabel>
						<Select
							value={statusFilter}
							label="Status"
							onChange={(e) => setStatusFilter(e.target.value)}>
							<MenuItem value="">All Statuses</MenuItem>
							{visitStatuses.map((status) => (
								<MenuItem key={status.id} value={status.id}>
									{status.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Paper
				elevation={0}
				sx={{ p: 2, mb: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
				<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
					<Typography variant="caption" color="text.secondary" fontWeight={600}>
						Legend:
					</Typography>
					<Chip
						size="small"
						label="Scheduled"
						sx={{ bgcolor: "#e3f2fd", border: "1px solid #1e88e5" }}
					/>
					<Chip
						size="small"
						label="Completed"
						sx={{ bgcolor: "#e8f5e9", border: "1px solid #43a047" }}
					/>
					<Chip
						size="small"
						label="Overdue"
						sx={{ bgcolor: "#ffebee", border: "1px solid #e53935" }}
					/>
					<Chip
						size="small"
						label="Completed Late"
						sx={{ bgcolor: "#fff3e0", border: "1px solid #fb8c00" }}
					/>
				</Box>
			</Paper>

			<Paper
				elevation={0}
				sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						px: 2,
						py: 1.5,
						borderBottom: "1px solid #e5e7eb",
						bgcolor: "#f9fafb",
						flexWrap: "wrap",
						gap: 1.5,
					}}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton
							size="small"
							onClick={viewMode === "month" ? handlePrevMonth : handlePrevDay}>
							<ChevronLeft size={20} />
						</IconButton>
						<Typography
							variant="subtitle1"
							fontWeight={600}
							sx={{ minWidth: 200, textAlign: "center" }}>
							{viewMode === "month"
								? currentMonth.format("MMMM YYYY")
								: currentDay.format("dddd, D MMMM YYYY")}
						</Typography>
						<IconButton
							size="small"
							onClick={viewMode === "month" ? handleNextMonth : handleNextDay}>
							<ChevronRight size={20} />
						</IconButton>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
						<ToggleButtonGroup
							size="small"
							value={viewMode}
							exclusive
							onChange={handleViewModeChange}
							aria-label="calendar view mode">
							<ToggleButton value="month" aria-label="month view">
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 0.5 }}>
									<CalendarDays size={16} />
									Month
								</Box>
							</ToggleButton>
							<ToggleButton value="day" aria-label="day view">
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 0.5 }}>
									<CalendarIcon size={16} />
									Day
								</Box>
							</ToggleButton>
						</ToggleButtonGroup>
						<Typography variant="body2" color="text.secondary">
							{viewMode === "month"
								? `${monthVisitsCount} visit${monthVisitsCount !== 1 ? "s" : ""} this month`
								: `${dayVisitsCount} visit${dayVisitsCount !== 1 ? "s" : ""} this day`}
						</Typography>
						<Chip label="Today" size="small" onClick={handleToday} clickable />
						{viewMode === "month" && selectedDay && (
							<Chip
								label="Open day view"
								size="small"
								color="primary"
								variant="outlined"
								onClick={handleOpenDayView}
								clickable
							/>
						)}
					</Box>
				</Box>

				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
						<CircularProgress size={32} />
					</Box>
				) : viewMode === "day" ? (
					<DayHourView
						dayVisits={dayViewVisits}
						employeeColorMap={employeeColorMap}
					/>
				) : (
					<Box sx={{ p: 2 }}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(7, 1fr)",
								gap: 0,
								mb: 1,
							}}>
							{WEEKDAYS.map((day) => (
								<Box key={day} sx={{ textAlign: "center", py: 1 }}>
									<Typography
										variant="caption"
										fontWeight={600}
										color="text.secondary">
										{day}
									</Typography>
								</Box>
							))}
						</Box>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(7, 1fr)",
								gap: "1px",
								bgcolor: "#e5e7eb",
								border: "1px solid #e5e7eb",
							}}>
							{calendarDays.map((day, dayIndex) => {
								const weekIndex = Math.floor(dayIndex / 7);
								const isRowExpanded = expandedRowIndex === weekIndex;
								const dateKey = day.format("YYYY-MM-DD");
								const dayVisits = visitsByDate[dateKey] || [];
								const isCurrentMonth = day.isSame(currentMonth, "month");
								const isToday = day.isSame(dayjs(), "day");
								const isSelected = selectedDay?.isSame(day, "day");
								const hasHiddenVisits =
									dayVisits.length > VISITS_COLLAPSED_LIMIT;
								const visibleVisits = isRowExpanded
									? dayVisits
									: dayVisits.slice(0, VISITS_COLLAPSED_LIMIT);

								return (
									<Box
										key={dateKey}
										onClick={() => handleDaySelect(day)}
										sx={{
											minHeight: isRowExpanded ? "auto" : 120,
											bgcolor: isSelected ? "#eef2ff" : "#fff",
											p: 0.75,
											cursor: "pointer",
											opacity: isCurrentMonth ? 1 : 0.45,
											"&:hover": {
												bgcolor: isSelected ? "#eef2ff" : "#f9fafb",
											},
										}}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 0.5,
											}}>
											<Typography
												variant="caption"
												fontWeight={isToday ? 700 : 500}
												sx={{
													width: 24,
													height: 24,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													borderRadius: "50%",
													bgcolor: isToday ? "#6366f1" : "transparent",
													color: isToday ? "#fff" : "text.primary",
												}}>
												{day.format("D")}
											</Typography>
											{dayVisits.length > 0 && (
												<Typography variant="caption" color="text.secondary">
													{dayVisits.length}
												</Typography>
											)}
										</Box>

										<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
											{visibleVisits.map((visit) => (
												<VisitCard
													key={visit.id}
													visit={visit}
													employeeColorMap={employeeColorMap}
												/>
											))}
											{!isRowExpanded && hasHiddenVisits && (
												<Typography
													variant="caption"
													color="primary"
													sx={{
														pl: 0.5,
														fontWeight: 600,
														cursor: "pointer",
														"&:hover": { textDecoration: "underline" },
													}}
													onClick={(e) => handleExpandRow(e, weekIndex, day)}>
													+{dayVisits.length - VISITS_COLLAPSED_LIMIT} more
												</Typography>
											)}
											{isRowExpanded && hasHiddenVisits && (
												<Typography
													variant="caption"
													color="primary"
													sx={{
														pl: 0.5,
														fontWeight: 600,
														cursor: "pointer",
														"&:hover": { textDecoration: "underline" },
													}}
													onClick={handleCollapseRow}>
													Show less
												</Typography>
											)}
										</Box>
									</Box>
								);
							})}
						</Box>
					</Box>
				)}
			</Paper>

			{employeeOptions.length > 0 && (
				<Paper
					elevation={0}
					sx={{ p: 2, mt: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Employee Colors
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						{employeeOptions.map((employee) => (
							<Chip
								key={employee.id}
								size="small"
								label={employee.name}
								sx={{
									borderLeft: `4px solid ${employeeColorMap[employee.id]}`,
									bgcolor: "#f9fafb",
								}}
							/>
						))}
					</Box>
				</Paper>
			)}

			{viewMode === "month" && selectedDay && (
				<Paper
					elevation={0}
					sx={{ p: 2, mt: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
					<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
						Visits on {selectedDay.format("dddd, D MMMM YYYY")}
					</Typography>
					{selectedDayVisits.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							No visits scheduled for this day.
						</Typography>
					) : (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							{selectedDayVisits.map((visit) => {
								const statusColor = getVisitStatusColor(visit);
								const employeeId = getVisitEmployeeId(visit);

								return (
									<Box
										key={visit.id}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 2,
											p: 1.5,
											borderRadius: 1,
											bgcolor: statusColor.bg,
											border: `1px solid ${statusColor.border}`,
											borderLeft: `4px solid ${employeeColorMap[employeeId] || "#6b7280"}`,
										}}>
										<Box>
											<Typography variant="body2" fontWeight={600}>
												{visit.schools?.name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{visit.visit_types?.name} ·{" "}
												{visit.visit_types?.visit_type || "Service"}
											</Typography>
										</Box>
										<Box sx={{ textAlign: "right" }}>
											<Typography variant="body2" fontWeight={600}>
												{getVisitEmployee(visit)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{visit.visit_status?.name || "Pending"}
												{visit.planned_date &&
													` · Planned ${formatVisitDateTime(visit.planned_date)}`}
												{visit.visited_date &&
													` · Visited ${formatVisitDateTime(visit.visited_date)}`}
											</Typography>
										</Box>
									</Box>
								);
							})}
						</Box>
					)}
				</Paper>
			)}
		</Box>
	);
};

export default SchoolVisitTracker;
